import { ErrorMessage } from "@hookform/error-message";
import { AnimatePresence, motion } from "framer-motion";
import { useEffect, useState } from "react";
import { RegisterOptions, useFormContext } from "react-hook-form";
import { dehtml } from "../../lib/utils";
import Error from "../Error";
import FieldNotice from "./FieldNotice";

interface FieldProps {
  className?: string;
  dataError?: string;
  dataLoading?: boolean;
  defaultValue?: string;
  disabled?: boolean;
  errorMessage?: string;
  fieldControl: (
    value: boolean | string | undefined,
    onChange: (value?: boolean | string) => void,
    inError: boolean,
    onBlur: () => void,
  ) => JSX.Element | null;
  fieldName: string;
  fieldValidations?: RegisterOptions;
  horizontal?: boolean;
  label?: string | Element;
  labelClassName?: string;
  labelColorClass?: string;
  loading?: boolean;
  noErrorMessage?: boolean;
  noLabel?: boolean;
  noticeMessage?: string | Element;
  onBlur?: (value?: boolean | string | undefined) => void;
  onChange?: (value?: boolean | string | undefined) => void;
  triggerValue?: any;
  validateOnBlur?: () => boolean;
  validateOnChange?: boolean;
}

function Field({
  className,
  dataError,
  dataLoading = false,
  defaultValue,
  disabled = false,
  errorMessage,
  fieldControl,
  fieldName,
  fieldValidations = {},
  horizontal = false,
  label,
  labelClassName = "",
  labelColorClass = "text-gray-700",
  loading,
  noErrorMessage,
  noLabel,
  noticeMessage,
  onBlur,
  onChange,
  triggerValue,
  validateOnBlur,
  validateOnChange = false,
}: FieldProps): JSX.Element | null {
  const formMethods = useFormContext();
  const { formState, watch, register, setValue, unregister, trigger } =
    formMethods;
  const value = watch(fieldName);

  const [fieldRegistered, setFieldRegistered] = useState(false);

  useEffect(() => {
    if (!dataLoading && !disabled) {
      register(fieldName, { ...fieldValidations, value });
      setFieldRegistered(true);
    }
    return () => {
      try {
        unregister(fieldName);
      } catch (e) {} // eslint-disable-line no-empty
    };
  }, [dataLoading]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (triggerValue > 0) trigger(fieldName);
  }, [triggerValue]);

  // Default the value if requested.
  useEffect(() => {
    if (defaultValue !== undefined && !value) {
      setValue(fieldName, defaultValue, {
        shouldDirty: true,
        shouldValidate: false,
      });
    }
  }, [fieldRegistered]); // eslint-disable-line react-hooks/exhaustive-deps

  function onValueBlur() {
    if (onBlur) onBlur(value);
    if (formState.isSubmitted && (!validateOnBlur || validateOnBlur()))
      trigger(fieldName);
  }

  function onValueChange(value?: boolean | string) {
    setValue(fieldName, value, {
      shouldDirty: true,
      shouldValidate: validateOnChange,
    });
    if (onChange) onChange(value);
  }

  if (dataError) {
    console.error(dataError);
    return null;
  }

  // Retrieve error by converting fieldnames with dots (a.b.c) into object keys (a => b => c)
  const error = fieldName
    ?.split(".")
    .reduce((p, c) => p?.[c], formState.errors);

  let dehtmlLabel = label;
  if (typeof label === "string" || label instanceof String) {
    dehtmlLabel = dehtml(label, true);
  }

  const loader = (
    <div className={className ? className : ""}>
      <label className="block text-sm font-medium text-gray-700">&nbsp;</label>
      <div className="relative mt-1 rounded-md shadow-sm">
        <div className="flex items-center justify-center w-full h-10 border-gray-400 rounded-md shadow-sm bg-gray-50 sm:text-base">
          <span className="relative text-2xl text-gray-300 -top-1">. . .</span>
        </div>
      </div>
    </div>
  );

  // Horizontal mode
  if (horizontal) {
    className += " sm:flex sm:flex-row sm:items-center sm:space-x-4";
    labelClassName += " sm:text-left ";
  }

  const group = (
    <motion.div
      initial={{ opacity: 1 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className={className ? className : ""}
    >
      {!noLabel && (
        <label
          className={`${labelClassName} ${labelColorClass} block text-base font-medium`}
        >
          {dehtmlLabel}&nbsp;
        </label>
      )}
      <div className="relative mt-1">
        {fieldControl(value, onValueChange, Boolean(error), onValueBlur)}
      </div>
      {!noErrorMessage && (
        <AnimatePresence>
          <ErrorMessage
            message={errorMessage}
            name={fieldName}
            render={({ message }) => (
              <Error className="mt-1" key={message} message={message} />
            )}
          />
        </AnimatePresence>
      )}
      <AnimatePresence>
        <FieldNotice
          className="mt-1"
          key={noticeMessage}
          message={noticeMessage}
        />
      </AnimatePresence>
    </motion.div>
  );

  return loading ? loader : group;
}

export default Field;
